/**
 * React Query hooks for the brokerage (e-broker / وساطة) contracts domain.
 *
 * All data access routes through `brokerageContractsRepository` (Nest-first;
 * no `supabase.from("brokerage_contracts")` here). Pattern mirrors the service-
 * requests hooks: enabled guard on companyId + invalidation on mutation.
 *
 * Salvaged from origin/feat/brokerage-contracts (PR #1344) and re-homed onto
 * the NestJS API built in Inc-1/2. The Supabase RPC calls for owner-status
 * are replaced with the Nest batched status endpoint.
 *
 * See: apps/api/src/brokerage-contracts/brokerage-contracts.controller.ts
 *      apps/shared/src/brokerage.ts
 *      src/repositories/brokerageContracts.ts
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  brokerageContractsRepository,
  type BrokerageContractListFilters,
} from "@/repositories/brokerageContracts";
import type {
  CreateBrokerageContract,
  UpdateBrokerageContract,
  LinkPropertyBrokerage,
} from "@dallali/shared";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
// UUID_REGEX guards the batched status endpoints' @IsUUID('all', { each: true }):
// it mirrors validator.js's `all` matcher (version nibble [1-8], variant [89ab],
// plus nil/max UUIDs), so we drop precisely the ids Nest would reject and nothing
// more — a looser filter once let one bad id 400 the whole batch and break the
// entire soft-warn surface (#2257).
import { isValidUUID, UUID_REGEX } from "@/lib/uuid";

const CACHE_KEY = "brokerage-contracts";
const STATUS_CACHE_KEY = "brokerage-property-status";

// ─── List ─────────────────────────────────────────────────────────────────────

/**
 * List brokerage contracts for the company. Filters (status, owner_id) are
 * optional and applied CLIENT-SIDE via React Query `select`: the Nest endpoint
 * returns every company contract and takes no params, so the cache key stays
 * stable (one fetch per company) while each caller gets its own filtered view.
 * enabled guard: !!companyId (prevents query before auth resolves).
 */
export function useBrokerageContracts(filters: BrokerageContractListFilters = {}) {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [CACHE_KEY, companyId],
    queryFn: () => brokerageContractsRepository.list(),
    enabled: !!companyId,
    select: (rows) =>
      rows.filter(
        (r) =>
          (!filters.status || r.status === filters.status) &&
          (!filters.owner_id || r.owner_id === filters.owner_id),
      ),
  });
}

/**
 * Permission-gated variant for the NON-BLOCKING soft-warn surfaces (dashboard
 * alert, owners-directory badge). Only fetches when the user can view contracts
 * (`contracts` permission) — fail-closed: no permission → no query, no warning,
 * no 403 noise. Shares the `[CACHE_KEY, companyId]` cache with
 * {@link useBrokerageContracts} so it never triggers a second network request.
 */
export function useBrokerageContractsGated() {
  const { companyId } = useAuth();
  const { hasPermission } = usePermissions();
  const canView = hasPermission("contracts");
  return useQuery({
    queryKey: [CACHE_KEY, companyId],
    queryFn: () => brokerageContractsRepository.list(),
    enabled: !!companyId && canView,
  });
}

// ─── Detail ───────────────────────────────────────────────────────────────────

/** Fetch a single brokerage contract by ID (includes property_links). */
export function useBrokerageContract(id: string | undefined) {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [CACHE_KEY, "detail", id, companyId],
    queryFn: () => brokerageContractsRepository.getById(id!),
    // isValidUUID: URL-driven id — see src/lib/uuid.ts (the #3297 class).
    enabled: !!id && !!companyId && isValidUUID(id),
  });
}

// ─── Create ───────────────────────────────────────────────────────────────────

/**
 * Create a new brokerage contract.
 * company_id is NOT in the payload — Nest derives it from the JWT.
 */
export function useCreateBrokerageContract() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();

  return useMutation({
    mutationFn: (payload: CreateBrokerageContract) => {
      if (!companyId) throw new Error("Company not found");
      return brokerageContractsRepository.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CACHE_KEY] });
    },
  });
}

// ─── Update ───────────────────────────────────────────────────────────────────

/** Update an existing brokerage contract (partial — all fields optional). */
export function useUpdateBrokerageContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: { id: string } & UpdateBrokerageContract) =>
      brokerageContractsRepository.update(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CACHE_KEY] });
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEY, "detail", variables.id],
      });
    },
  });
}

// ─── Delete ───────────────────────────────────────────────────────────────────

/** Soft-delete a brokerage contract. */
export function useDeleteBrokerageContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => brokerageContractsRepository.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CACHE_KEY] });
    },
  });
}

// ─── Property links ───────────────────────────────────────────────────────────

/** Link a property to a contract for a given operation type. */
export function useLinkBrokerageProperty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contractId,
      link,
    }: {
      contractId: string;
      link: LinkPropertyBrokerage;
    }) => brokerageContractsRepository.linkProperty(contractId, link),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CACHE_KEY] });
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEY, "detail", variables.contractId],
      });
    },
  });
}

/** Unlink a property from a contract (removes the junction row). */
export function useUnlinkBrokerageProperty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contractId,
      linkId,
    }: {
      contractId: string;
      linkId: string;
    }) => brokerageContractsRepository.unlinkProperty(contractId, linkId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CACHE_KEY] });
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEY, "detail", variables.contractId],
      });
    },
  });
}

// ─── Property status (Nest endpoint — no Supabase RPC) ───────────────────────

/**
 * Effective brokerage standing for a single property.
 * Drives the non-blocking soft-warn surfaces (Phase-1: flag only).
 */
export function usePropertyBrokerageStatus(propertyId: string | undefined) {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [STATUS_CACHE_KEY, "single", propertyId, companyId],
    queryFn: () =>
      brokerageContractsRepository.getStatusForProperty(propertyId!),
    enabled: !!propertyId && !!companyId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Batched variant — pass an array of property IDs, get back one entry per
 * property. Use this in list/directory views to avoid N+1 fan-out.
 * Returns a Record<propertyId, PropertyBrokerageStatus>.
 */
export function useBatchPropertyBrokerageStatus(propertyIds: string[]) {
  const { companyId } = useAuth();
  // Defensive UUID filter: the Nest status endpoint validates `property_ids`
  // with @IsUUID('all', { each: true }) and 400s the WHOLE batch if any id is
  // malformed (legacy/demo rows, an optimistic temp id, a stray ""). The
  // soft-warn badges are non-blocking by design (Inc-4) — a stray id must never
  // break them or spam Sentry. Drop non-UUIDs and de-dupe before querying.
  const sortedIds = [...new Set(propertyIds.filter((id) => UUID_REGEX.test(id)))].sort();
  return useQuery({
    queryKey: [STATUS_CACHE_KEY, "batch", sortedIds, companyId],
    queryFn: () =>
      brokerageContractsRepository.getBatchStatus(sortedIds),
    enabled: !!companyId && sortedIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });
}

// ─── Unit status ─────────────────────────────────────────────────────────────

/**
 * Effective brokerage standing for a single UNIT.
 *
 * A unit is covered by its OWN unit-scoped contracts plus the whole-property
 * ones it inherits; a contract scoped to a SIBLING unit does not count. The
 * result's `coverage` ('unit' | 'property' | 'none') says which of the two is
 * driving the verdict, so the UI can explain WHY the unit is covered.
 */
export function useUnitBrokerageStatus(unitId: string | undefined) {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [STATUS_CACHE_KEY, "unit", unitId, companyId],
    queryFn: () => brokerageContractsRepository.getStatusForUnit(unitId!),
    enabled: !!unitId && !!companyId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Batched variant for unit grids/list views — one entry per unit, no N+1 fan-out.
 * Returns a Record<unitId, UnitBrokerageStatus>.
 *
 * Same defensive UUID filter as the property batch above, for the same reason
 * (#2239): one malformed id must never 400 the whole batch and blank every
 * badge on the page.
 */
export function useBatchUnitBrokerageStatus(unitIds: string[]) {
  const { companyId } = useAuth();
  const sortedIds = [...new Set(unitIds.filter((id) => UUID_REGEX.test(id)))].sort();
  return useQuery({
    queryKey: [STATUS_CACHE_KEY, "unit-batch", sortedIds, companyId],
    queryFn: () => brokerageContractsRepository.getBatchUnitStatus(sortedIds),
    enabled: !!companyId && sortedIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });
}
